import { Box, Grid, Paper, Typography, Stack, Button, alpha } from "@mui/material";
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import BalanceIcon from '@mui/icons-material/Balance';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import { ExternalUrls } from "../../../../lib/constants";
import { PricingTiers } from "../../../../lib/pricing-data";
import { PricingContent } from "../../../../lib/pricing-content";
import { ProofBarData } from "../../../../lib/social-proof";
import { getFAQs } from "../../../../lib/faqs";
import { useFAQSchema, useProductSchema, ProductOffer } from "../../../../lib/seo";
import { formatPrice } from "../../../../lib/pricing-utils";
import { useCurrency } from "../../../../contexts/CurrencyContext";
import { BrandColors } from "@neumind/shared/branding";

import { Section } from "../../../ui/Section";
import { SectionHeader } from "../../../ui/SectionHeader";
import { FadeIn } from "../../../ui/FadeIn";
import { PricingCard } from "../../../ui/PricingCard";
import { PricingTabHeader } from "../PricingTabHeader";
import { PricingEnterpriseCallout } from "../PricingEnterpriseCallout";
import { PricingAddonStrip } from "../PricingAddonStrip";
import { PricingSocialProofSection } from "../../PricingSocialProofSection";
import { FAQSection } from "../../FAQSection";
import { CallToActionCard } from "../../../ui/CallToActionCard";
import { usePostHog } from "../../../widgets/PostHogProvider";

const PAYER_ICONS = [
    { icon: BusinessIcon, color: BrandColors.primary.main },
    { icon: PersonIcon, color: BrandColors.success.main },
    { icon: BalanceIcon, color: BrandColors.warning.main },
];

export function PricingProfessionalsView() {
    const { currency } = useCurrency();
    const { capture } = usePostHog();
    const content = PricingContent.professionals;
    const faqs = getFAQs('pricing-professional');
    useFAQSchema(faqs);

    // Product schema for SEO
    const productOffers: ProductOffer[] = [
        {
            name: 'Neumind Pro (Clinician Account)',
            description: 'Free clinician dashboard with AI therapy builder and unlimited team seats.',
            price: 0,
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        },
        {
            name: 'Neumind Client License',
            description: 'Per-client license for active rehabilitation with clinician connection.',
            price: PricingTiers.active.monthly[currency],
            priceCurrency: currency,
            availability: 'InStock',
            url: '/pricing'
        }
    ];
    useProductSchema(productOffers);

    return (
        <>
            <Section centered py={6} maxWidth="xl">
                {/* HEADER */}
                <PricingTabHeader
                    title={content.header.title}
                    description={content.header.description}
                    proofBarItems={ProofBarData.professionals}
                    textMb={2}
                />

                {/* Clinician Account - Free Tier */}
                <Box sx={{ maxWidth: 600, mx: 'auto', mb: 6 }}>
                    <FadeIn delay={100} direction="up">
                        <PricingCard
                            overhead="For Healthcare Professionals"
                            title="Neumind Pro"
                            caption="For Healthcare Professionals"
                            price="Free"
                            priceSuffix=""
                            description="for your team & draft clients"
                            features={PricingTiers.professional.clinicianFeatures || []}
                            ctaText="Create Free Account"
                            ctaHref={ExternalUrls.dashboardSignup}
                            ctaSubText="No credit card required"
                            highlighted={true}
                            highlightText="Free Tier"
                        />
                    </FadeIn>
                </Box>

                {/* Who Pays Section Header */}
                <SectionHeader
                    overline="Client Licenses"
                    title="Choose how client licenses are paid"
                    subtitle="Mix and match across your caseload. Each option connects seamlessly to your free clinician account."
                    centered
                    titleVariant="h2"
                    sx={{ mb: 4 }}
                />

                {/* 3 Payer Strategy Cards */}
                <Grid container spacing={3} justifyContent="center">
                    {content.strategies.map((strategy, index) => {
                        const { icon: Icon, color } = PAYER_ICONS[index];

                        // Calculate price for this strategy
                        let displayPrice = '';
                        if (strategy.pricingTierId) {
                            const tier = PricingTiers[strategy.pricingTierId];
                            if (tier && 'monthly' in tier) {
                                displayPrice = formatPrice(tier.monthly[currency], currency);
                            }
                        }

                        return (
                            <Grid item xs={12} md={4} key={strategy.id}>
                                <FadeIn delay={100 + index * 100} direction="up" style={{ height: '100%' }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            borderRadius: 3,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 12px 24px -10px ${alpha(color, 0.2)}`,
                                                borderColor: color,
                                            },
                                        }}
                                    >
                                        {/* Icon & Title */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    bgcolor: alpha(color, 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Icon sx={{ color, fontSize: 24 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h5" fontWeight={700}>
                                                    {strategy.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {strategy.description}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Price (if applicable) */}
                                        {displayPrice && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="h4" fontWeight={700} color={color}>
                                                    {displayPrice}
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        /mo per client
                                                    </Typography>
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Key Features */}
                                        <Stack spacing={1} sx={{ mb: 3, flexGrow: 1 }}>
                                            {strategy.features.slice(0, 3).map((feature, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <CheckIcon sx={{ color, fontSize: 18, mt: 0.3 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>{feature.title}:</strong> {feature.desc}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>

                                        {/* CTA */}
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            href={ExternalUrls.dashboardSignup}
                                            onClick={() =>
                                                capture('cta_clicked', {
                                                    location: 'pricing_professionals_license_options',
                                                    cta_title: strategy.title,
                                                    cta_label: 'Get Started',
                                                    cta_href: ExternalUrls.dashboardSignup,
                                                    cta_type: 'primary',
                                                    cta_external: true,
                                                })
                                            }
                                            endIcon={<ChevronRightIcon />}
                                            sx={{
                                                borderColor: color,
                                                color: color,
                                                '&:hover': {
                                                    bgcolor: alpha(color, 0.05),
                                                    borderColor: color,
                                                },
                                            }}
                                        >
                                            Get Started
                                        </Button>
                                    </Paper>
                                </FadeIn>
                            </Grid>
                        );
                    })}
                </Grid>

                <Box id="enterprise-section" sx={{ mt: 6 }}>
                    <PricingEnterpriseCallout />
                </Box>
            </Section>

            {/* Stats Section */}
            <Section variant="dark" py={8}>
                <SectionHeader
                    title="Empowering clinicians"
                    subtitle="Tools that extend your reach and improve client outcomes."
                    variant="dark"
                    centered
                    sx={{ mb: { xs: 4, md: 8 } }}
                />
                <PricingSocialProofSection
                    userType={['professional']}
                    metricIds={['taskAdherence', 'timeSaving']}
                    bgVariant="dark"
                />
            </Section>

            {/* Pricing Add-on Strip */}
            <Section variant="muted" py={8}>
                <FadeIn direction="up">
                    <SectionHeader
                        overline="Optional Add-ons"
                        title="Enhance your implementation"
                        subtitle="Optional services to help you get the most out of Neumind."
                        centered
                        titleVariant="h2"
                    />
                </FadeIn>
                <PricingAddonStrip data={content.addOns} />
            </Section>

            {/* Common Questions */}
            <Section variant="default" py={8}>
                <SectionHeader
                    title="Common Questions"
                    centered
                    sx={{ mb: 8 }}
                />
                <FAQSection faqs={faqs} />
            </Section>

            {/* CTA */}
            <Section variant="muted" py={12}>
                <CallToActionCard
                    location="pricing_professionals_bottom_cta"
                    title="Ready to extend your clinical reach?"
                    description="Join hundreds of clinicians using Neumind to improve client outcomes."
                    primaryCTA={{ label: "Create Free Account", href: ExternalUrls.dashboardSignup, external: true }}
                    secondaryCTA={{ label: "Contact Us", href: "/contact-us" }}
                />
            </Section>
        </>
    );
}
